import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, IconButton, Button, TextField, InputAdornment, Grid, Alert, CircularProgress,
  MenuItem, Select, FormControl, InputLabel, Checkbox,
} from '@mui/material';
import {
  Search, Refresh, Description, FileDownload, Print,
} from '@mui/icons-material';
import { transactionsAdminAPI } from '../services/api';
import { fetchCompanyInfo } from '../services/companyInfo';

const formatPhone = (v) => {
  const digits = (v || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('02')) {
    if (digits.length <= 5) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    if (digits.length <= 9) return `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`;
    return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6, 10)}`;
  }
  if (digits.length === 11) return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return digits.slice(0, 11);
};
const fmtMoney = (n) => n == null || n === '' ? '-' : Number(n).toLocaleString() + '원';
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('ko-KR') : '-';

const STATUS_LABEL = {
  before_dispatch: '배차전', dispatch_ready: '배차준비', broadcasting: '호출중',
  assigned: '배차완료', in_progress: '배송중', completed: '완료', cancelled: '주문취소',
};
const PRICE_LABEL = {
  pending_price: '금액검토', payment_pending: '결재대기', deferred_payment: '추후결재',
  payment_completed: '결재완료', refund_pending: '환불대기', refund_completed: '환불완료',
  no_payment: '결재금액없음',
};
const SETTLED_PRICE = ['payment_completed', 'no_payment'];

const parseJson = (v) => {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch { return []; }
};

// 예상 수익 = 제시가(고객) - 확정가(기사). 둘 중 하나라도 없으면 null
const calcProfit = (o) => {
  const customer = Number(o.client_offered_price) || 0;
  const driver = Number(o.confirmed_price) || 0;
  return customer && driver ? customer - driver : null;
};

const escHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));

export default function TransactionsPage() {
  const now = new Date();
  const [filter, setFilter] = useState({
    year: String(now.getFullYear()),
    month: String(now.getMonth() + 1),
    settle: '',
    business_number: '',
    customer_id: '',
    q: '',
  });
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ companies: [], contacts: [] });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [selected, setSelected] = useState([]);

  const load = () => {
    setLoading(true); setErr('');
    const params = {};
    Object.entries(filter).forEach(([k, v]) => { if (v !== '' && v != null) params[k] = v; });
    transactionsAdminAPI.getAll(params)
      .then((res) => setRows(res.data?.data || []))
      .catch((e) => setErr(e.response?.data?.message || '로드 실패'))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    transactionsAdminAPI.getFilters()
      .then((res) => setFilters(res.data?.data || { companies: [], contacts: [] }))
      .catch(() => {});
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.year, filter.month, filter.settle, filter.business_number, filter.customer_id]);

  // 검색 (q) 는 디바운스 없이 버튼/엔터로 발화
  const handleQEnter = (e) => { if (e.key === 'Enter') load(); };

  const years = (() => {
    const ys = []; const y = now.getFullYear();
    for (let i = y; i >= y - 4; i--) ys.push(String(i));
    return ys;
  })();
  const months = Array.from({ length: 12 }, (_, i) => String(i + 1));

  const toggle = (id) => setSelected((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const toggleAll = () => {
    const ids = rows.map((r) => r.order_id);
    setSelected(selected.length === ids.length ? [] : ids);
  };

  const selectedRows = useMemo(() => rows.filter((r) => selected.includes(r.order_id)), [rows, selected]);
  const supplyAmount = selectedRows.reduce((s, r) => s + (Number(r.confirmed_price) || Number(r.client_offered_price) || 0), 0);
  const taxAmount = Math.round(supplyAmount * 0.1);
  const totalAmount = supplyAmount + taxAmount;
  const profitSum = selectedRows.reduce((s, r) => s + (calcProfit(r) || 0), 0);

  // 선택 행이 동일 사업자번호인지 (세금계산서 발행 가능 조건)
  const selectedBusinessNumbers = useMemo(() => {
    const set = new Set(selectedRows.map((r) => r.customer_business_number || ''));
    set.delete('');
    return Array.from(set);
  }, [selectedRows]);
  const canIssueTax = selectedRows.length > 0 && selectedBusinessNumbers.length === 1;

  // CSV 다운로드
  const downloadCsv = () => {
    const target = selectedRows.length > 0 ? selectedRows : rows;
    const headers = ['거래일자','주문번호','업체명','사업자번호','대표자','상차지','하차지','차종','톤수','제시가','확정가','수익','배차상태','결재상태','거래처 담당자','운전자'];
    const data = target.map((o) => {
      const vTypes = parseJson(o.vehicle_types);
      const vWeights = parseJson(o.vehicle_weights);
      return [
        fmtDate(o.created_at),
        o.order_number,
        o.customer_company || o.customer_name || '',
        o.customer_business_number || '',
        o.customer_ceo || '',
        o.origin_address || '',
        o.destination_address || '',
        (vTypes.length ? vTypes : [o.required_vehicle_type]).filter(Boolean).join('/'),
        (vWeights.length ? vWeights : (o.required_weight ? [o.required_weight + 't'] : [])).join('/'),
        o.client_offered_price || '',
        o.confirmed_price || '',
        calcProfit(o) ?? '',
        STATUS_LABEL[o.status] || o.status || '',
        PRICE_LABEL[o.price_status] || o.price_status || '',
        o.customer_name || '',
        o.driver_name ? `${o.driver_name} ${o.driver_vehicle_number || ''}`.trim() : '',
      ];
    });
    const csv = [headers, ...data].map((r) => r.map((c) => {
      const s = String(c ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `거래실적_${filter.year}${String(filter.month).padStart(2,'0')}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  // 인쇄
  const printRows = () => {
    const target = selectedRows.length > 0 ? selectedRows : rows;
    if (target.length === 0) return;
    const rowsHtml = target.map((o) => {
      const vTypes = parseJson(o.vehicle_types);
      const vt = (vTypes.length ? vTypes : [o.required_vehicle_type]).filter(Boolean).join('/');
      return `<tr>
        <td>${escHtml(fmtDate(o.created_at))}</td>
        <td>${escHtml(o.order_number)}</td>
        <td>${escHtml(o.customer_company || o.customer_name || '')}</td>
        <td>${escHtml(o.customer_business_number || '')}</td>
        <td>${escHtml(o.origin_address || '')}</td>
        <td>${escHtml(o.destination_address || '')}</td>
        <td>${escHtml(vt)}</td>
        <td style="text-align:right">${o.confirmed_price ? Number(o.confirmed_price).toLocaleString() : (o.client_offered_price ? Number(o.client_offered_price).toLocaleString() : '')}</td>
        <td>${escHtml(PRICE_LABEL[o.price_status] || o.price_status || '')}</td>
        <td>${escHtml(o.customer_name || '')}</td>
      </tr>`;
    }).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>거래실적</title>
      <style>
        @page { size: A4 landscape; margin: 12mm; }
        body { font-family: 'Noto Sans KR', sans-serif; }
        h1 { font-size: 18px; margin: 0 0 4px; }
        .meta { font-size: 11px; color: #666; margin-bottom: 10px; }
        table { width: 100%; border-collapse: collapse; font-size: 11px; }
        th, td { border: 1px solid #999; padding: 4px 6px; }
        thead { background: #f0f0f0; }
      </style></head><body>
      <h1>거래실적 ${filter.year}년 ${filter.month}월${filter.settle === 'settled' ? ' (완결)' : filter.settle === 'unsettled' ? ' (미결)' : ''}</h1>
      <div class="meta">출력일: ${new Date().toLocaleDateString('ko-KR')} · 총 ${target.length}건</div>
      <table>
        <thead><tr><th>거래일자</th><th>주문번호</th><th>업체</th><th>사업자번호</th><th>상차지</th><th>하차지</th><th>차종</th><th>금액</th><th>결재</th><th>거래처 담당자</th></tr></thead>
        <tbody>${rowsHtml}</tbody>
      </table>
      <script>window.onload = () => setTimeout(() => window.print(), 300);</script>
      </body></html>`;
    const w = window.open('', '_blank');
    if (!w) return alert('팝업이 차단되었습니다.');
    w.document.open(); w.document.write(html); w.document.close();
  };

  // 세금계산서 발행 (선택 거래, 동일 업체)
  const issueTaxInvoice = async () => {
    if (!canIssueTax) return alert('선택한 거래의 업체가 하나여야 발행할 수 있습니다.');
    const customer = {
      company_name: selectedRows[0].customer_company,
      business_number: selectedRows[0].customer_business_number,
      ceo_name: selectedRows[0].customer_ceo,
      address: selectedRows[0].customer_address,
      phone: selectedRows[0].customer_phone,
      office_phone: selectedRows[0].customer_office_phone,
      email: selectedRows[0].customer_email,
    };
    let company = {};
    try { company = await fetchCompanyInfo(true) || {}; } catch { /* ignore */ }
    const issuedDate = new Date().toISOString().slice(0, 10);
    const itemsHtml = selectedRows.map((o, i) => {
      const supply = Number(o.confirmed_price) || Number(o.client_offered_price) || 0;
      const tax = Math.round(supply * 0.1);
      return `<tr>
        <td style="text-align:center">${i + 1}</td>
        <td style="text-align:center">${escHtml(fmtDate(o.created_at))}</td>
        <td>화물 운송 (${escHtml(o.order_number)})</td>
        <td>${escHtml(o.origin_address || '')} → ${escHtml(o.destination_address || '')}</td>
        <td style="text-align:center">1</td>
        <td style="text-align:right">${supply.toLocaleString()}</td>
        <td style="text-align:right">${supply.toLocaleString()}</td>
        <td style="text-align:right">${tax.toLocaleString()}</td>
      </tr>`;
    }).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>세금계산서</title>
      <style>
        @page { size: A4; margin: 14mm; }
        body { font-family: 'Noto Sans KR', sans-serif; color: #111; }
        h1 { text-align: center; font-size: 22px; margin: 0 0 4px; letter-spacing: 4px; }
        .sub { text-align: center; color: #666; font-size: 11px; margin-bottom: 14px; }
        .parties { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
        .parties th, .parties td { border: 1px solid #999; padding: 6px 8px; font-size: 11px; vertical-align: middle; }
        .parties th { background: #f0f0f0; width: 70px; text-align: center; }
        .party-title { background: #1565c0; color: #fff; text-align: center; font-weight: 700; padding: 6px; }
        .items { width: 100%; border-collapse: collapse; margin-bottom: 14px; font-size: 11px; }
        .items th, .items td { border: 1px solid #999; padding: 6px; }
        .items thead { background: #f5f5f5; }
        .summary { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
        .summary td { border: 1px solid #999; padding: 8px; font-size: 12px; }
        .summary td.label { background: #f5f5f5; text-align: center; font-weight: 700; width: 12%; }
        .summary td.amount { text-align: right; font-weight: 700; font-size: 14px; }
        .total-row td.amount { color: #c62828; font-size: 16px; }
        .footer { font-size: 10px; color: #666; text-align: right; margin-top: 12px; }
        .actions { text-align: center; margin: 16px 0; }
        .actions button { padding: 8px 20px; font-size: 13px; cursor: pointer; }
        @media print { .actions { display: none; } }
      </style></head><body>
      <div class="actions"><button onclick="window.print()">인쇄 / PDF 저장</button> <button onclick="window.close()">닫기</button></div>
      <h1>세 금 계 산 서</h1>
      <div class="sub">공급받는자 보관용 · 발행일: ${issuedDate}</div>
      <table class="parties">
        <colgroup><col style="width:9%"><col style="width:41%"><col style="width:9%"><col style="width:41%"></colgroup>
        <tr><td class="party-title" colspan="2">공급자</td><td class="party-title" colspan="2">공급받는자</td></tr>
        <tr><th>등록번호</th><td>${escHtml(company.business_number || '')}</td><th>등록번호</th><td>${escHtml(customer.business_number || '')}</td></tr>
        <tr><th>상호</th><td>${escHtml(company.company_name || '')}</td><th>상호</th><td>${escHtml(customer.company_name || '')}</td></tr>
        <tr><th>대표자</th><td>${escHtml(company.ceo_name || '')}</td><th>대표자</th><td>${escHtml(customer.ceo_name || '')}</td></tr>
        <tr><th>주소</th><td>${escHtml(company.company_address || '')}</td><th>주소</th><td>${escHtml(customer.address || '')}</td></tr>
        <tr><th>업태/종목</th><td>운수업 / 화물운송</td><th>연락처</th><td>${escHtml(customer.office_phone || customer.phone || '')}</td></tr>
      </table>
      <table class="items">
        <thead><tr><th style="width:5%">No</th><th style="width:10%">공급일자</th><th style="width:22%">품목</th><th>규격(경로)</th><th style="width:5%">수량</th><th style="width:10%">단가</th><th style="width:12%">공급가액</th><th style="width:10%">세액</th></tr></thead>
        <tbody>${itemsHtml}</tbody>
      </table>
      <table class="summary">
        <tr>
          <td class="label">공급가액 합계</td><td class="amount">${supplyAmount.toLocaleString()}원</td>
          <td class="label">세액(10%)</td><td class="amount">${taxAmount.toLocaleString()}원</td>
        </tr>
        <tr class="total-row">
          <td class="label">합계 금액</td><td class="amount" colspan="3">${totalAmount.toLocaleString()}원</td>
        </tr>
        <tr>
          <td class="label">입금계좌</td><td>${escHtml(company.account_holder ? `${company.bank_name || ''} ${company.account_number || ''} (${company.account_holder})` : '')}</td>
          <td class="label">비고</td><td>이 금액을 청구함</td>
        </tr>
      </table>
      <div class="footer">※ 내부 출력/PDF 보관용입니다. 정식 전자세금계산서는 발행대행사를 통해 별도 진행해 주세요.</div>
      <script>window.onload = () => setTimeout(() => window.print(), 300);</script>
      </body></html>`;
    const w = window.open('', '_blank');
    if (!w) return alert('팝업이 차단되었습니다.');
    w.document.open(); w.document.write(html); w.document.close();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight={700}>거래실적</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={load}>새로고침</Button>
          <Button variant="outlined" startIcon={<FileDownload />} onClick={downloadCsv} disabled={rows.length === 0}>엑셀</Button>
          <Button variant="outlined" startIcon={<Print />} onClick={printRows} disabled={rows.length === 0}>인쇄</Button>
          <Button variant="contained" color="warning" startIcon={<Description />} onClick={issueTaxInvoice} disabled={!canIssueTax}
            title={!canIssueTax && selectedRows.length > 0 ? '선택한 거래의 업체가 1개여야 발행 가능' : ''}>
            세금계산서 발행 {selectedRows.length > 0 && `(${selectedRows.length})`}
          </Button>
        </Box>
      </Box>

      {/* 필터 */}
      <Card sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>년</InputLabel>
              <Select label="년" value={filter.year} onChange={(e) => setFilter({ ...filter, year: e.target.value })}>
                <MenuItem value="">전체</MenuItem>
                {years.map((y) => <MenuItem key={y} value={y}>{y}년</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>월</InputLabel>
              <Select label="월" value={filter.month} onChange={(e) => setFilter({ ...filter, month: e.target.value })}>
                <MenuItem value="">전체</MenuItem>
                {months.map((m) => <MenuItem key={m} value={m}>{m}월</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>결재</InputLabel>
              <Select label="결재" value={filter.settle} onChange={(e) => setFilter({ ...filter, settle: e.target.value })}>
                <MenuItem value="">전체</MenuItem>
                <MenuItem value="settled">완결</MenuItem>
                <MenuItem value="unsettled">미결</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>업체 (사업자번호)</InputLabel>
              <Select label="업체 (사업자번호)" value={filter.business_number}
                onChange={(e) => setFilter({ ...filter, business_number: e.target.value })}>
                <MenuItem value="">전체</MenuItem>
                {filters.companies.map((c) => (
                  <MenuItem key={c.business_number} value={c.business_number}>
                    {c.company_name || c.name} ({c.business_number})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>거래처 담당자</InputLabel>
              <Select label="거래처 담당자" value={filter.customer_id}
                onChange={(e) => setFilter({ ...filter, customer_id: e.target.value })}>
                <MenuItem value="">전체</MenuItem>
                {filters.contacts.map((c) => (
                  <MenuItem key={c.customer_id} value={c.customer_id}>
                    {c.company_name ? `${c.company_name} · ${c.name}` : c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth size="small" placeholder="주문번호/주소/업체명 검색 (Enter)"
              value={filter.q}
              onChange={(e) => setFilter({ ...filter, q: e.target.value })}
              onKeyDown={handleQEnter}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
                endAdornment: <InputAdornment position="end"><Button size="small" onClick={load}>검색</Button></InputAdornment>,
              }}
            />
          </Grid>
        </Grid>
      </Card>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {/* 선택 합계 */}
      {selectedRows.length > 0 && (
        <Card sx={{ p: 1.5, mb: 2, bgcolor: '#fff8e1', border: '1px solid #ffb74d' }}>
          <Typography variant="body2">
            선택 <strong>{selectedRows.length}건</strong> ·
            공급가액 <strong>{supplyAmount.toLocaleString()}원</strong> ·
            부가세 <strong>{taxAmount.toLocaleString()}원</strong> ·
            합계 <strong style={{ color: '#c62828' }}>{totalAmount.toLocaleString()}원</strong> ·
            예상 수익 <strong style={{ color: profitSum >= 0 ? '#2e7d32' : '#d32f2f' }}>{profitSum >= 0 ? '+' : ''}{profitSum.toLocaleString()}원</strong>
            {selectedBusinessNumbers.length > 1 && (
              <span style={{ marginLeft: 12, color: '#d32f2f' }}>· ⚠ 사업자번호 {selectedBusinessNumbers.length}개 — 세금계산서는 1개 업체씩 발행</span>
            )}
          </Typography>
        </Card>
      )}

      <Card>
        <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 360px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < rows.length}
                    checked={rows.length > 0 && selected.length === rows.length}
                    onChange={toggleAll}
                  />
                </TableCell>
                <TableCell>거래일자</TableCell>
                <TableCell>주문번호</TableCell>
                <TableCell>업체</TableCell>
                <TableCell>사업자번호</TableCell>
                <TableCell>상차지</TableCell>
                <TableCell>하차지</TableCell>
                <TableCell>차종</TableCell>
                <TableCell align="right">제시가</TableCell>
                <TableCell align="right">확정가</TableCell>
                <TableCell align="right">수익</TableCell>
                <TableCell>배차상태</TableCell>
                <TableCell>결재상태</TableCell>
                <TableCell>거래처 담당자</TableCell>
                <TableCell>운전자</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={15} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={15} align="center" sx={{ py: 4, color: 'text.secondary' }}>해당 조건의 거래가 없습니다.</TableCell></TableRow>
              ) : rows.map((o) => {
                const isSel = selected.includes(o.order_id);
                const isSettled = SETTLED_PRICE.includes(o.price_status);
                const vTypes = parseJson(o.vehicle_types);
                return (
                  <TableRow key={o.order_id} hover selected={isSel}
                    sx={{ cursor: 'pointer' }} onClick={() => toggle(o.order_id)}>
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={isSel} onChange={() => toggle(o.order_id)} />
                    </TableCell>
                    <TableCell>{fmtDate(o.created_at)}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{o.order_number}</TableCell>
                    <TableCell>{o.customer_company || o.customer_name || '-'}</TableCell>
                    <TableCell>{o.customer_business_number || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={o.origin_address}>{o.origin_address}</TableCell>
                    <TableCell sx={{ maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={o.destination_address}>{o.destination_address}</TableCell>
                    <TableCell>{(vTypes.length ? vTypes : [o.required_vehicle_type]).filter(Boolean).join('/') || '-'}</TableCell>
                    <TableCell align="right">{fmtMoney(o.client_offered_price)}</TableCell>
                    <TableCell align="right">{fmtMoney(o.confirmed_price)}</TableCell>
                    <TableCell align="right">
                      {(() => {
                        const profit = calcProfit(o);
                        if (profit === null) return '-';
                        return (
                          <span style={{ color: profit >= 0 ? '#2e7d32' : '#d32f2f', fontWeight: 700 }}>
                            {profit >= 0 ? '+' : ''}{profit.toLocaleString()}원
                          </span>
                        );
                      })()}
                    </TableCell>
                    <TableCell><Chip size="small" label={STATUS_LABEL[o.status] || o.status} /></TableCell>
                    <TableCell><Chip size="small" label={PRICE_LABEL[o.price_status] || o.price_status || '-'} variant="outlined" color={isSettled ? 'success' : 'default'} /></TableCell>
                    <TableCell>{o.customer_name || '-'}</TableCell>
                    <TableCell>{o.driver_name ? `${o.driver_name} ${o.driver_vehicle_number || ''}`.trim() : '-'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}
